import React, { useEffect, useState } from "react";
import providers from "lib/providers";
import UserProfileModal from "components/UserProfileModal";
import { t } from "locales";
import "./style.scss";

interface MentionData {
  userId: string;
  screenName: string;
}

interface ProfileTarget {
  userId?: string;
  screenName?: string;
}

interface Props {
  text: string;
  searchQuery?: string;
}

// Plain-name alternative allows internal `.`, `+` and `-` between word runs
// (usernames like "i.kospryshin+4"), each separator requiring a following word
// char so a trailing "." / "+" at sentence end stays outside the mention.
const COMBINED_REGEX = /(?:^|(?<=\s))@(?:\[(\d+)\]|([\w]+(?:[.+-][\w]+)*))/g;

// Two-tier cache for resolved mention names.
//
// Why: the bubble initially renders `@User <id>` as a placeholder, then
// `useEffect` fetches the real screen name from
// `providers.UserPublicDataProvider.getById` and re-renders with it. When
// the resolved name is longer than the placeholder, the inline text re-wraps
// — a measurable layout shift that propagates through Virtuoso's spacers
// (and can yank a mid-list reader's viewport content even with selective
// scroll-anchoring on images). Caching means: after the FIRST session that
// resolves a user, every subsequent chat re-open / bubble re-render reads
// the name synchronously from cache — no fetch, no placeholder→real swap,
// no layout shift.
//
//   - Hot (in-memory `Map`): per-page-session, instant on re-render.
//   - Warm (`localStorage`): per-origin, TTL'd (7 days for hits). Survives
//     page reloads / chat re-opens. Probed once on module load; graceful
//     fall-through to in-memory only when localStorage is unavailable.

const nameCache = new Map<string, string>();

const NAME_PERSIST_KEY_PREFIX = "mentionName:v1:";
const NAME_PERSIST_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

const NAME_PERSIST_AVAILABLE = (() => {
  try {
    const probe = "__mentionName_probe__";
    localStorage.setItem(probe, "1");
    localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
})();

interface NamePersistedEntry {
  name: string;
  ts: number;
}

function readPersistedName(userId: string): string | null {
  if (!NAME_PERSIST_AVAILABLE) return null;
  try {
    const raw = localStorage.getItem(NAME_PERSIST_KEY_PREFIX + userId);
    if (!raw) return null;
    const entry: NamePersistedEntry = JSON.parse(raw);
    if (Date.now() - entry.ts > NAME_PERSIST_TTL_MS) {
      localStorage.removeItem(NAME_PERSIST_KEY_PREFIX + userId);
      return null;
    }
    return entry.name;
  } catch {
    return null;
  }
}

function writePersistedName(userId: string, name: string): void {
  if (!NAME_PERSIST_AVAILABLE) return;
  try {
    localStorage.setItem(
      NAME_PERSIST_KEY_PREFIX + userId,
      JSON.stringify({ name, ts: Date.now() }),
    );
  } catch {
    // Quota exceeded or other failure — drop silently, keep hot cache only.
  }
}

// Bold **text** | Italic *text* | Strikethrough ~~text~~ | Auto-linked URL.
// Trailing punctuation (.,;:!?)]) stays outside the URL so it renders as text.
const INLINE_STYLE_REGEX = /(\*\*(.+?)\*\*|\*(.+?)\*|~~(.+?)~~|((?:https?:\/\/|www\.)[^\s<>"]*[^\s<>".,;:!?)\]]))/g;

function highlightText(str: string, query: string): React.ReactNode {
  if (!query.trim()) return str;
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = str.split(new RegExp(`(${escaped})`, "gi"));
  if (parts.length === 1) return str;
  const q = query.toLowerCase();
  return parts.map((part, i) =>
    part.toLowerCase() === q ? (
      <mark key={i} className="message-bubble__search-highlight">{part}</mark>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  );
}

function createKeyGen() {
  let counter = 0;
  return () => `md-${counter++}`;
}

function parseInlineMarkdown(text: string, searchQuery: string | undefined, keygen: () => string): React.ReactNode[] {
  const results: React.ReactNode[] = [];

  // Inline code
  const codeRegex = /`([^`]+)`/g;
  let lastIdx = 0;
  let m;
  const segments: { start: number; end: number; node: React.ReactNode }[] = [];

  while ((m = codeRegex.exec(text)) !== null) {
    segments.push({
      start: m.index,
      end: m.index + m[0].length,
      node: <code key={keygen()} className="mention-text__code">{m[1]}</code>,
    });
  }

  if (segments.length === 0) {
    return [applyInlineStyles(text, searchQuery, keygen)];
  }

  for (const seg of segments) {
    if (seg.start > lastIdx) {
      results.push(applyInlineStyles(text.substring(lastIdx, seg.start), searchQuery, keygen));
    }
    results.push(seg.node);
    lastIdx = seg.end;
  }
  if (lastIdx < text.length) {
    results.push(applyInlineStyles(text.substring(lastIdx), searchQuery, keygen));
  }
  return results;
}

function applyInlineStyles(text: string, searchQuery: string | undefined, keygen: () => string): React.ReactNode {
  const regex = new RegExp(INLINE_STYLE_REGEX.source, "g");
  const parts: React.ReactNode[] = [];
  let lastIdx = 0;
  let m;

  while ((m = regex.exec(text)) !== null) {
    if (m.index > lastIdx) {
      const plain = text.substring(lastIdx, m.index);
      parts.push(searchQuery ? highlightText(plain, searchQuery) : plain);
    }
    if (m[2] !== undefined) {
      parts.push(<strong key={keygen()} className="mention-text__bold">{m[2]}</strong>);
    } else if (m[3] !== undefined) {
      parts.push(<em key={keygen()} className="mention-text__italic">{m[3]}</em>);
    } else if (m[4] !== undefined) {
      parts.push(<del key={keygen()} className="mention-text__strikethrough">{m[4]}</del>);
    } else if (m[5] !== undefined) {
      const url = m[5];
      const href = url.startsWith("www.") ? `https://${url}` : url;
      parts.push(
        <a
          key={keygen()}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="mention-text__link"
          onClick={(e) => e.stopPropagation()}
        >
          {url}
        </a>
      );
    }
    lastIdx = m.index + m[0].length;
  }

  if (lastIdx === 0) {
    return searchQuery ? highlightText(text, searchQuery) : text;
  }
  if (lastIdx < text.length) {
    const plain = text.substring(lastIdx);
    parts.push(searchQuery ? highlightText(plain, searchQuery) : plain);
  }
  return <React.Fragment key={keygen()}>{parts}</React.Fragment>;
}

function parseMarkdown(text: string, searchQuery: string | undefined, keygen: () => string): React.ReactNode[] {
  // Handle code blocks first: ```...```
  const codeBlockRegex = /```([\s\S]*?)```/g;
  const blocks: React.ReactNode[] = [];
  let lastIdx = 0;
  let m;

  while ((m = codeBlockRegex.exec(text)) !== null) {
    if (m.index > lastIdx) {
      blocks.push(...parseLines(text.substring(lastIdx, m.index), searchQuery, keygen));
    }
    const codeContent = m[1];
    blocks.push(
      <div key={keygen()} className="mention-text__code-block-wrap">
        <button
          className="mention-text__code-copy"
          type="button"
          onClick={() => {
            navigator.clipboard.writeText(codeContent).catch(() => {});
            const btn = document.activeElement as HTMLButtonElement;
            if (btn) { btn.textContent = t("Copied!"); setTimeout(() => { btn.textContent = t("Copy"); }, 1500); }
          }}
        >{t("Copy")}</button>
        <pre className="mention-text__code-block"><code>{codeContent}</code></pre>
      </div>
    );
    lastIdx = m.index + m[0].length;
  }

  if (lastIdx === 0) {
    return parseLines(text, searchQuery, keygen);
  }
  if (lastIdx < text.length) {
    blocks.push(...parseLines(text.substring(lastIdx), searchQuery, keygen));
  }
  return blocks;
}

function parseLines(text: string, searchQuery: string | undefined, keygen: () => string): React.ReactNode[] {
  // Handle blockquotes at line level
  const lines = text.split("\n");
  const result: React.ReactNode[] = [];

  for (let i = 0; i < lines.length; i++) {
    if (i > 0) result.push("\n");
    const line = lines[i];
    if (line.startsWith("> ")) {
      result.push(
        <span key={keygen()} className="mention-text__blockquote">
          {parseInlineMarkdown(line.substring(2), searchQuery, keygen)}
        </span>
      );
    } else {
      result.push(...parseInlineMarkdown(line, searchQuery, keygen));
    }
  }
  return result;
}

function MentionText({ text, searchQuery }: Props) {
  // Seed state from the in-memory + warm caches so that on first render
  // we already have any names this session (or this user) has previously
  // resolved — no placeholder→real swap, no layout shift.
  const [resolvedNames, setResolvedNames] = useState<Record<string, string>>(
    () => {
      const seed: Record<string, string> = {};
      const regex = new RegExp(COMBINED_REGEX.source, "g");
      let match;
      while ((match = regex.exec(text)) !== null) {
        const userId = match[1];
        if (!userId || seed[userId]) continue;
        const cached = nameCache.get(userId) ?? readPersistedName(userId);
        if (cached) {
          seed[userId] = cached;
          // Promote warm-cache hit to hot cache for the rest of the session.
          if (!nameCache.has(userId)) nameCache.set(userId, cached);
        }
      }
      return seed;
    },
  );
  const [profileTarget, setProfileTarget] = useState<ProfileTarget | null>(null);

  useEffect(() => {
    const seenIds = new Set<string>();
    const idsToFetch: string[] = [];
    let match;
    const regex = new RegExp(COMBINED_REGEX.source, "g");
    while ((match = regex.exec(text)) !== null) {
      const userId = match[1];
      if (!userId || seenIds.has(userId)) continue;
      seenIds.add(userId);
      // Skip ids we already have from cache (seeded into resolvedNames
      // by useState initialiser above).
      if (nameCache.has(userId)) continue;
      idsToFetch.push(userId);
    }
    if (idsToFetch.length === 0) return;

    let cancelled = false;
    Promise.all(
      idsToFetch.map((id) =>
        providers.UserPublicDataProvider.getById({ userId: id })
          .then((u: any) => ({ userId: id, screenName: u?.screenName || `User ${id}` }))
          .catch(() => ({ userId: id, screenName: `User ${id}` }))
      )
    ).then((results: MentionData[]) => {
      if (cancelled) return;
      // Populate both cache tiers so sibling bubbles + future renders + future
      // sessions all benefit. We only persist real names (not the `User <id>`
      // fallback) so a transient API error doesn't sticky-fail caching.
      results.forEach((r) => {
        nameCache.set(r.userId, r.screenName);
        if (!r.screenName.startsWith("User ")) {
          writePersistedName(r.userId, r.screenName);
        }
      });
      setResolvedNames((prev) => {
        const next: Record<string, string> = { ...prev };
        results.forEach((r) => { next[r.userId] = r.screenName; });
        return next;
      });
    });
    return () => { cancelled = true; };
  }, [text]);

  const parts = React.useMemo(() => {
    const keygen = createKeyGen();
    const result: React.ReactNode[] = [];
    let lastIndex = 0;
    let match;
    const regex = new RegExp(COMBINED_REGEX.source, "g");

    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        const segment = text.substring(lastIndex, match.index);
        result.push(...parseMarkdown(segment, searchQuery, keygen));
      }
      const userId = match[1];
      const screenName = match[2];
      const displayName = userId
        ? (resolvedNames[userId] || `User ${userId}`)
        : screenName;

      result.push(
        <span
          key={`mention-${match.index}`}
          className="mention-text__highlight"
          onClick={() =>
            setProfileTarget(userId ? { userId } : { screenName })
          }
        >
          @{displayName}
        </span>
      );
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
      const segment = text.substring(lastIndex);
      result.push(...parseMarkdown(segment, searchQuery, keygen));
    }
    return result;
  }, [text, searchQuery, resolvedNames]);

  return (
    <>
      {parts.length === 0 ? text : parts}
      {profileTarget && (
        <UserProfileModal
          userId={profileTarget.userId}
          screenName={profileTarget.screenName}
          onClose={() => setProfileTarget(null)}
        />
      )}
    </>
  );
}

export default MentionText;
